import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';

import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordComponent, PasswordComponentValue } from '../controls/components/basic/password/password.component';
import { ButtonPrimaryDirective } from '../controls/directives/button-primary.directive';
import { ButtonSubmitDirective } from '../controls/directives/button-submit.directive';
import { ButtonComponent } from '../controls/components/basic/button/button.component';
import { AuthenticationService } from '../shared/authentication/authentication.service';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationInsightsService } from '../shared/insights/application-insights.service';

@Component({
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  imports: [ReactiveFormsModule, PasswordComponent, ButtonComponent, ButtonPrimaryDirective, ButtonSubmitDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnInit {
  private appInsightsService = inject(ApplicationInsightsService);
  private router = inject(Router);
  private modalService = inject(GenericModalService);
  private authenticationService = inject(AuthenticationService);

  key = input.required<string>();

  isSubmitting = signal(false);
  isSuccess = signal(false);

  resetPasswordForm = new FormGroup({
    newPassword: new FormControl<PasswordComponentValue | null>(null, [Validators.required])
  });

  ngOnInit(): void {
    this.appInsightsService.logEvent('PasswordReset', { state: 'ResetActivated' });
  }

  async onSubmit(): Promise<void> {
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.isSubmitting.set(true);
    const passwordValue = this.resetPasswordForm.value.newPassword;
    const newPassword = passwordValue?.value || null;

    try {
      await this.authenticationService.resetPassword(this.key(), newPassword);
      this.isSuccess.set(true);
      this.appInsightsService.logEvent('PasswordReset', { state: 'Success' });

      setTimeout(() => {
        void this.navigateToLogin();
      }, 3000);
    }
    catch (err) {
      if ((err instanceof HttpErrorResponse) && (err.status === 400)) {
        await this.modalService.messageDirect('Invalid request', 'The password reset link is invalid or has expired. Please request a new password reset email.');
      }
      else {
        console.error('Error resetting password', err);
        await this.modalService.messageDirect('Failed', 'An unexpected error occurred while resetting your password. Please request a new password reset email.');
      }
      await this.navigateToLogin();
    }
    finally {
      this.isSubmitting.set(false);
    }
  }

  async navigateToLogin(): Promise<void> {
    await this.router.navigate(['/login']);
  }

  async onCancel(): Promise<void> {
    await this.router.navigate(['/login']);
  }
}
